import classNames from "classnames";
import { capitalize } from "lodash";
import { useCallback } from "react";
import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeItemContext,
  createStepNode,
} from "~/components/build/Show/routes/list/-components/TreeNode";
import { createGroupStepNodeForState } from "~/components/build/Show/routes/list/-components/TreeNode/GroupStepItem";
import { STATE_HEADER_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { useStepsForStateQuery } from "~/components/build/Show/lib/useSteps";
import { useFilterStore, type FilterByOption } from "~/components/build/Show/lib/useFilterStore";
import { filterKey } from "~/components/build/Show/lib/useExpandedIds";
import Icon from "~/components/shared/Icon";
import { FilterStateIcon } from "~/components/build/Show/lib/FilterStateIcon";
import { isGroupStep } from "~/lib/pipeline";
import { StepRow } from "~/components/build/Show/routes/list/-components/StepRow";
import { Badge } from "~/components/build/Show/routes/list/-components/Badge";

interface Props extends TreeItemContext {
  state: FilterByOption;
  count: number;
}

export function StateHeaderItem({ state, count, ...props }: Props) {
  const { build } = useBuild();
  const stepsGroupedByState = useBuildPageStore((store) => store.stepsGroupedByState);
  const setExpandedStates = useBuildPreferencesStore((store) => store.setExpandedStates);
  const expandedStates = useBuildPreferencesStore((store) => store.expandedStates || []);
  const setFilterStateOverride = useBuildPageStore((store) => store.setFilterStateOverride);
  const filters = useFilterStore((store) => store.filterBy);
  const isFilterMatched = filters.includes(state);
  const query = useStepsForStateQuery(
    build,
    state,
    {
      enabled:
        props.collapsibleState === CollapsibleState.Expanded ||
        props.collapsibleState === CollapsibleState.Full,
    },
    { excludeGroupSteps: Features.BuildPageFlattenStateGroups },
  );

  const toggleState = useCallback(() => {
    const isExpanded =
      props.collapsibleState === CollapsibleState.Expanded ||
      props.collapsibleState === CollapsibleState.Full;
    const nextState = isExpanded ? CollapsibleState.Collapsed : CollapsibleState.Expanded;

    if (isFilterMatched) {
      // During a filter session, record the override without touching localStorage
      setFilterStateOverride(state, { filterKey: filterKey(filters), state: nextState });
    } else {
      // No filter: persist to localStorage
      if (isExpanded) {
        setExpandedStates(expandedStates.filter((expandedState) => expandedState !== state));
      } else {
        setExpandedStates([...expandedStates, state]);
      }
    }
  }, [
    props.collapsibleState,
    isFilterMatched,
    state,
    filters,
    setFilterStateOverride,
    expandedStates,
    setExpandedStates,
  ]);

  return (
    <div className="relative w-full">
      <button
        onClick={toggleState}
        data-testid={`${state}-group`}
        style={{ height: STATE_HEADER_HEIGHT }}
        className={classNames(
          "flex w-full cursor-pointer items-center self-start rounded-md bg-slate-100 py-1 pl-1.5 hover:bg-gray-100 dark:hover:bg-gray-100",
          {
            "animate-none": query.isLoading && !stepsGroupedByState.get(state)?.length,
          },
        )}
      >
        <StepRow
          chevron={
            <span
              aria-hidden="true"
              className="flex h-6 w-6 select-none items-center justify-center rounded-sm hover:bg-purple-100"
            >
              <Icon
                icon="heroicons/outline/chevron-right"
                className="h-3 w-3 transition-transform duration-150 group-aria-expanded:rotate-90"
              />
            </span>
          }
          icon={<FilterStateIcon filter={state} />}
          label={
            <div className="inline-flex items-center gap-x-2">
              <span className="text-charcoal-600 text-sm font-medium">{capitalize(state)}</span>
              <Badge className="tabular-nums" data-testid={`${state}-group-count`}>
                {count} {count === 1 ? "step" : "steps"}
              </Badge>
            </div>
          }
        />
      </button>
    </div>
  );
}

export const createStateHeaderNode = (
  state: FilterByOption,
  count: number,
  build: Build,
): TreeNode => {
  return {
    id: state,
    height: STATE_HEADER_HEIGHT,
    sticky: true,
    render: (props) => <StateHeaderItem key={state} state={state} count={count} {...props} />,
    childNodes: (collapsibleState: CollapsibleState) => {
      if (collapsibleState === CollapsibleState.Expanded) {
        const steps = useBuildPageStore.getState().stepsGroupedByState.get(state) || [];

        if (Features.BuildPageFlattenStateGroups) {
          // Render all leaf steps flat under the state header, ignoring any
          // parent group steps.
          return steps
            .filter((step) => !isGroupStep(step))
            .map((step) => createStepNode(step, build));
        }

        // Filter out grouped leaf steps — they render as children of their group node.
        return steps
          .filter((step) => isGroupStep(step) || !step.groupUuid)
          .map((step) => {
            if (isGroupStep(step)) {
              return createGroupStepNodeForState(step, build, state);
            }
            return createStepNode(step, build);
          });
      }
      return [];
    },
  };
};
